// Utility functions for time slot management

/**
 * Generate time slots in 30-minute intervals from 9:30 AM to 11:30 PM
 * @returns {Array} Array of time slot objects with value and label
 */
export const generateTimeSlots = () => {
  const slots = [];
  const startHour = 9;
  const startMinute = 30;
  const endHour = 23;
  const endMinute = 30;

  let currentHour = startHour;
  let currentMinute = startMinute;

  while (currentHour < endHour || (currentHour === endHour && currentMinute <= endMinute)) {
    const timeString = formatTime(currentHour, currentMinute);
    const nextTimeString = formatTime(
      currentMinute + 30 >= 60 ? currentHour + 1 : currentHour,
      currentMinute + 30 >= 60 ? (currentMinute + 30) % 60 : currentMinute + 30
    );

    slots.push({
      value: `${timeString} - ${nextTimeString}`,
      label: `${timeString} - ${nextTimeString}`,
      startTime: timeString,
      endTime: nextTimeString
    });

    // Move to next 30-minute slot
    currentMinute += 30;
    if (currentMinute >= 60) {
      currentHour += 1;
      currentMinute = 0;
    }
  }

  return slots;
};

/**
 * Format time in 12-hour format with AM/PM
 * @param {number} hour - Hour (0-23)
 * @param {number} minute - Minute (0-59)
 * @returns {string} Formatted time string
 */
const formatTime = (hour, minute) => {
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const minuteStr = minute.toString().padStart(2, '0');
  const hourStr = displayHour.toString().padStart(2, '0');
  return `${hourStr}:${minuteStr} ${ampm}`;
};

/**
 * Check if a time slot is disabled based on current time and date
 * @param {Object} slot - Time slot object
 * @param {string} selectedDate - Selected date in YYYY-MM-DD format
 * @param {string} today - Today's date in YYYY-MM-DD format
 * @returns {boolean} True if slot should be disabled
 */
export const isTimeSlotDisabled = (slot, selectedDate, today) => {
  // If selected date is today, check if time has passed
  if (selectedDate === today) {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    // Parse slot start time
    const [timeStr] = slot.startTime.split(' ');
    const [hourStr, minuteStr] = timeStr.split(':');
    const hour = parseInt(hourStr);
    const minute = parseInt(minuteStr);
    const isPM = slot.startTime.includes('PM');
    
    let slotHour = hour;
    if (isPM && hour !== 12) {
      slotHour = hour + 12;
    } else if (!isPM && hour === 12) {
      slotHour = 0;
    }
    
    const slotTime = slotHour * 60 + minute;
    
    // Disable if slot time has passed
    return slotTime <= currentTime;
  }
  
  return false;
};

/**
 * Parse cutoff time string (e.g., "18 Hours") to hours
 * @param {string} cutoffTime - Cutoff time string like "18 Hours"
 * @returns {number} Hours as number
 */
export const parseCutoffTime = (cutoffTime) => {
  if (!cutoffTime) return 0;
  const match = cutoffTime.match(/(\d+)\s*Hours?/i);
  return match ? parseInt(match[1]) : 0;
};

/**
 * Check if a date is within cutoff time
 * @param {string} selectedDate - Selected date in YYYY-MM-DD format
 * @param {string} today - Today's date in YYYY-MM-DD format
 * @param {string} cutoffTime - Cutoff time string like "18 Hours"
 * @returns {boolean} True if date is within cutoff time (far enough in advance)
 */
export const isDateWithinCutoff = (selectedDate, today, cutoffTime) => {
  if (!cutoffTime) return true;
  
  const cutoffHours = parseCutoffTime(cutoffTime);
  const selectedDateObj = new Date(selectedDate + 'T00:00:00');
  const todayObj = new Date(today + 'T00:00:00');
  
  // Calculate difference in days
  const diffTime = selectedDateObj.getTime() - todayObj.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  // If it's the same day, check if we have enough hours left
  if (diffDays === 0) {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const hoursFromNow = 24 - currentHour - (currentMinute / 60);
    
    // Check if we have at least the cutoff hours remaining
    return hoursFromNow >= cutoffHours;
  }
  
  // If it's a past date, it's not within cutoff
  if (diffDays < 0) {
    return false;
  }
  
  // If it's a future date, check if it's far enough in advance
  // Convert cutoff hours to days for comparison
  const cutoffDays = cutoffHours / 24;
  return diffDays >= cutoffDays;
};

/**
 * Check if a time slot is blocked by unavailability schedule
 * @param {Object} slot - Time slot object
 * @param {string} selectedDate - Selected date in YYYY-MM-DD format
 * @param {Array} unavailabilitySchedule - Array of unavailability objects
 * @returns {boolean} True if slot is blocked
 */
export const isTimeSlotBlocked = (slot, selectedDate, unavailabilitySchedule = []) => {
  if (!unavailabilitySchedule || unavailabilitySchedule.length === 0) {
    return false;
  }
  
  // Convert selected date to MM-DD-YYYY format for comparison
  const dateObj = new Date(selectedDate);
  const formattedDate = `${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}-${dateObj.getFullYear()}`;
  
  // Find unavailability entry for this date
  const unavailabilityEntry = unavailabilitySchedule.find(entry => 
    entry.unavailable_date === formattedDate
  );
  
  if (!unavailabilityEntry) {
    return false;
  }
  
  // If whole day is blocked
  if (unavailabilityEntry.unavailable_all_day) {
    return true;
  }
  
  // Check if slot is in blocked time slots
  if (unavailabilityEntry.unavailable_slot && Array.isArray(unavailabilityEntry.unavailable_slot)) {
    return unavailabilityEntry.unavailable_slot.some(blockedSlot => {
      // Compare time slots (format: "05:00 PM - 05:30 PM")
      return blockedSlot === slot.value;
    });
  }
  
  return false;
};

/**
 * Get available time slots for a given date with package constraints
 * @param {string} selectedDate - Selected date in YYYY-MM-DD format
 * @param {string} today - Today's date in YYYY-MM-DD format
 * @param {Object} packageData - Package data with cutoff time and unavailability schedule
 * @returns {Array} Available time slots
 */
export const getAvailableTimeSlots = (selectedDate, today, packageData = null) => {
  const allSlots = generateTimeSlots();
  
  return allSlots.filter(slot => {
    // Check if slot is disabled due to time passing
    if (isTimeSlotDisabled(slot, selectedDate, today)) {
      return false;
    }
    
    // Check cutoff time if package data is provided
    if (packageData && packageData.cutt_off_time_in_hours) {
      // First check if the date itself is within cutoff
      if (!isDateWithinCutoff(selectedDate, today, packageData.cutt_off_time_in_hours)) {
        return false;
      }
      
      // Then check if the specific time slot is within cutoff
      if (!isTimeSlotWithinCutoff(slot, selectedDate, today, packageData.cutt_off_time_in_hours)) {
        return false;
      }
    }
    
    // Check unavailability schedule if package data is provided
    if (packageData && packageData.unavailability_schedule) {
      if (isTimeSlotBlocked(slot, selectedDate, packageData.unavailability_schedule)) {
        return false;
      }
    }
    
    return true;
  });
};

/**
 * Get available time slots for multiple packages (intersection of all available slots)
 * @param {string} selectedDate - Selected date in YYYY-MM-DD format
 * @param {string} today - Today's date in YYYY-MM-DD format
 * @param {Array} packages - Array of package data
 * @param {boolean} includeDisabled - If true, includes disabled slots with disabled flag
 * @returns {Array} Available time slots that work for all packages (or all slots with disabled flag if includeDisabled)
 */
export const getAvailableTimeSlotsForPackages = (selectedDate, today, packages = [], includeDisabled = false) => {
  if (!packages || packages.length === 0) {
    const slots = generateTimeSlots();
    return includeDisabled ? slots.map(slot => ({ ...slot, disabled: false })) : slots;
  }
  
  const allSlots = generateTimeSlots();
  
  if (includeDisabled) {
    // Return all slots with disabled flag
    return allSlots.map(slot => {
      // Check if slot is disabled due to time passing
      if (isTimeSlotDisabled(slot, selectedDate, today)) {
        return { ...slot, disabled: true };
      }
      
      // Check if slot is available for ALL packages
      const isAvailable = packages.every(pkg => {
        // Check cutoff time if package has it
        if (pkg.cuttOffTimeInHours) {
          // First check if the date itself is within cutoff
          if (!isDateWithinCutoff(selectedDate, today, pkg.cuttOffTimeInHours)) {
            return false;
          }
          
          // Then check if the specific time slot is within cutoff
          if (!isTimeSlotWithinCutoff(slot, selectedDate, today, pkg.cuttOffTimeInHours)) {
            return false;
          }
        }
        
        // Check unavailability schedule if package has it
        if (pkg.unavailabilitySchedule && pkg.unavailabilitySchedule.length > 0) {
          if (isTimeSlotBlocked(slot, selectedDate, pkg.unavailabilitySchedule)) {
            return false;
          }
        }
        
        return true;
      });
      
      return { ...slot, disabled: !isAvailable };
    });
  } else {
    // Original behavior - filter out unavailable slots
    return allSlots.filter(slot => {
      // Check if slot is disabled due to time passing
      if (isTimeSlotDisabled(slot, selectedDate, today)) {
        return false;
      }
      
      // Check if slot is available for ALL packages
      return packages.every(pkg => {
        // Check cutoff time if package has it
        if (pkg.cuttOffTimeInHours) {
          // First check if the date itself is within cutoff
          if (!isDateWithinCutoff(selectedDate, today, pkg.cuttOffTimeInHours)) {
            return false;
          }
          
          // Then check if the specific time slot is within cutoff
          if (!isTimeSlotWithinCutoff(slot, selectedDate, today, pkg.cuttOffTimeInHours)) {
            return false;
          }
        }
        
        // Check unavailability schedule if package has it
        if (pkg.unavailabilitySchedule && pkg.unavailabilitySchedule.length > 0) {
          if (isTimeSlotBlocked(slot, selectedDate, pkg.unavailabilitySchedule)) {
            return false;
          }
        }
        
        return true;
      });
    });
  }
};

/**
 * Check if a whole day is blocked
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {Array} blockedDates - Array of blocked dates
 * @returns {boolean} True if whole day is blocked
 */
export const isWholeDayBlocked = (date, blockedDates = []) => {
  return blockedDates.includes(date);
};

/**
 * Check if a date is blocked by unavailability schedule
 * @param {string} selectedDate - Selected date in YYYY-MM-DD format
 * @param {string} today - Today's date in YYYY-MM-DD format
 * @param {Object} packageData - Package data with cutoff time and unavailability schedule
 * @returns {boolean} True if date is blocked
 */
export const isDateBlocked = (selectedDate, today, packageData = null) => {
  // Check cutoff time first
  if (packageData && packageData.cutt_off_time_in_hours) {
    if (!isDateWithinCutoff(selectedDate, today, packageData.cutt_off_time_in_hours)) {
      return true;
    }
  }
  
  // Check unavailability schedule
  if (packageData && packageData.unavailability_schedule) {
    // Convert selected date to MM-DD-YYYY format for comparison
    const dateObj = new Date(selectedDate);
    const formattedDate = `${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}-${dateObj.getFullYear()}`;
    
    // Find unavailability entry for this date
    const unavailabilityEntry = packageData.unavailability_schedule.find(entry => 
      entry.unavailable_date === formattedDate
    );
    
    if (unavailabilityEntry && unavailabilityEntry.unavailable_all_day) {
      return true;
    }
  }
  
  return false;
};

/**
 * Check if a date is blocked for any package in the packages array
 * @param {string} selectedDate - Selected date in YYYY-MM-DD format
 * @param {string} today - Today's date in YYYY-MM-DD format
 * @param {Array} packages - Array of package data
 * @returns {boolean} True if date is blocked for all packages
 */
export const isDateBlockedForPackages = (selectedDate, today, packages = []) => {
  if (!packages || packages.length === 0) {
    return false;
  }
  
  // Check if date is blocked for ALL packages
  return packages.every(pkg => {
    // Check cutoff time first
    if (pkg.cuttOffTimeInHours) {
      if (!isDateWithinCutoff(selectedDate, today, pkg.cuttOffTimeInHours)) {
        return true;
      }
    }
    
    // Check unavailability schedule
    if (pkg.unavailabilitySchedule && pkg.unavailabilitySchedule.length > 0) {
      // Convert selected date to MM-DD-YYYY format for comparison
      const dateObj = new Date(selectedDate);
      const formattedDate = `${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}-${dateObj.getFullYear()}`;
      
      // Find unavailability entry for this date
      const unavailabilityEntry = pkg.unavailabilitySchedule.find(entry => 
        entry.unavailable_date === formattedDate
      );
      
      if (unavailabilityEntry && unavailabilityEntry.unavailable_all_day) {
        return true;
      }
    }
    
    return false;
  });
};

/**
 * Check if a time slot is within cutoff time constraints
 * @param {Object} slot - Time slot object
 * @param {string} selectedDate - Selected date in YYYY-MM-DD format
 * @param {string} today - Today's date in YYYY-MM-DD format
 * @param {string} cutoffTime - Cutoff time string like "18 Hours"
 * @returns {boolean} True if slot is within cutoff time
 */
export const isTimeSlotWithinCutoff = (slot, selectedDate, today, cutoffTime) => {
  if (!cutoffTime) return true;
  
  const cutoffHours = parseCutoffTime(cutoffTime);
  
  // If it's not today, the slot is valid
  if (selectedDate !== today) {
    return true;
  }
  
  // For today, check if the slot start time is at least cutoff hours away
  const now = new Date();
  const currentTime = now.getHours() * 60 + now.getMinutes();
  
  // Parse slot start time
  const [timeStr] = slot.startTime.split(' ');
  const [hourStr, minuteStr] = timeStr.split(':');
  const hour = parseInt(hourStr);
  const minute = parseInt(minuteStr);
  const isPM = slot.startTime.includes('PM');
  
  let slotHour = hour;
  if (isPM && hour !== 12) {
    slotHour = hour + 12;
  } else if (!isPM && hour === 12) {
    slotHour = 0;
  }
  
  const slotTime = slotHour * 60 + minute;
  const cutoffTimeMinutes = cutoffHours * 60;
  
  // Check if slot is at least cutoff hours away
  return (slotTime - currentTime) >= cutoffTimeMinutes;
};

/**
 * Get today's date in YYYY-MM-DD format
 * @returns {string} Today's date
 */
export const getTodayDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
